import { APP_HOST } from '@utils/apiHooks';

import {
  formatWebsiteLink,
  getFullFacebookHttpLink,
  getFullInstagramHttpLink,
  getFullLinkedInHttpLink,
  getFullTwitterHttpLink,
  getFullXingHttpLink,
} from '@utils/fn';

import {
  FooterRootContainer,
  FooterLinksContainer,
  TermsAndCopyrightsContainer,
  Logo,
  CompanyDescription,
  Column,
  ColumnLogo,
  ColumnTitle,
  SocialLinkColumnTitle,
  LinkItem,
  ContactInfo,
  SocialIcons,
  OptionsContainer,
  FootNoteOption,
  CopyrightText,
  FooterContainer,
  CompanyDescriptionContainer,
  FooterContentContainer,
} from './styles';

import Icon from '@components/Icon';
import { Row } from '@components/LinearLayout';
import React, { useMemo } from 'react';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { ROUTES } from '@constants/routes';
import { useMobileWidth } from '@utils/hooks';
import digitalBusinessLogo from '@assets/images/blog/logo.svg';
import { Blog } from '@hubs101/booking-api-skd-client/lib/types';
import { useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setIsGdprOpen } from '@store/slices/configSlice';

const BlogsFooter = ({ blogs }: { blogs: Blog[] }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMobileWidth();
  const dispatch = useDispatch();

  const location = useLocation();
  const slug = location.pathname.split(`${t('industry-hubs-url')}/`)[1];
  const currentBlog = useMemo(
    () => blogs?.find(blog => blog.slug === slug),
    [blogs, slug]
  );

  const contact = useMemo(
    () =>
      currentBlog
        ? currentBlog?.account?.contact
        : blogs?.[0]?.account?.contact,
    [currentBlog, blogs]
  );

  const socialMediaIcons = [
    contact?.facebook && {
      icon: 'social_facebook',
      onClick: () =>
        window.open(getFullFacebookHttpLink(contact.facebook), '_blank'),
    },
    contact?.twitter && {
      icon: 'social_twitter',
      onClick: () =>
        window.open(getFullTwitterHttpLink(contact.twitter), '_blank'),
    },
    contact?.youtube && {
      icon: 'social_youtube',
      onClick: () => window.open(formatWebsiteLink(contact.youtube), '_blank'),
    },
    contact?.linkedin && {
      icon: 'social_linkedin',
      onClick: () =>
        window.open(getFullLinkedInHttpLink(contact.linkedin), '_blank'),
    },
    contact?.instagram && {
      icon: 'social_instagram',
      onClick: () =>
        window.open(getFullInstagramHttpLink(contact.instagram), '_blank'),
    },
    contact?.xing && {
      icon: 'social_xing',
      onClick: () => {
        window.open(getFullXingHttpLink(contact.xing), '_blank');
      },
    },
  ];

  return (
    <FooterRootContainer>
      <FooterContainer>
        <FooterContentContainer>
          <ColumnLogo>
            <Logo
              src={digitalBusinessLogo}
              alt={currentBlog ? `${currentBlog.name} Logo` : 'WeConect Logo'}
            />

            <CompanyDescriptionContainer>
              <CompanyDescription>{t('blogs.footer-title')}</CompanyDescription>
            </CompanyDescriptionContainer>
          </ColumnLogo>

          <FooterLinksContainer>
            <Column data-section="footer-second-col">
              <ColumnTitle>{t('blogs.footer-second-col.title')}</ColumnTitle>
              <LinkItem
                onClick={() => {
                  window.location.href = `${APP_HOST}${t('events-url')}`;
                }}
              >
                {t('blogs.footer-second-col.item-1')}
              </LinkItem>
              <LinkItem
                onClick={() => {
                  window.location.href = `${t(ROUTES.OUR_PORTFOLIOS)}`;
                }}
              >
                {t('blogs.footer-second-col.item-2')}
              </LinkItem>
              <LinkItem
                onClick={() => {
                  window.location.href = `${APP_HOST}${t('industry-hubs-url')}`;
                }}
              >
                {t('blogs.footer-second-col.item-3')}
              </LinkItem>
            </Column>

            <Column data-section="footer-third-col">
              <ColumnTitle>{t('blogs.footer-third-col.title')}</ColumnTitle>

              {blogs?.map((blog, index) => {
                return (
                  <React.Fragment
                    key={`footer-blog-link-fragment-${blog.slug}`}
                  >
                    <LinkItem
                      key={`footer-blog-link-${blog.slug}`}
                      onClick={() => {
                        window.location.href = `${APP_HOST}${t('industry-hubs-url')}/${blog.slug}`;
                      }}
                    >
                      {blog.name}
                    </LinkItem>

                    {isMobile && index !== (blogs?.length || 0) - 1 && (
                      <span> - </span>
                    )}
                  </React.Fragment>
                );
              })}
            </Column>

            <Column>
              <SocialLinkColumnTitle>{t('social-links')}</SocialLinkColumnTitle>

              <Row $gap="1rem" margin="1rem 0 0 0">
                <SocialIcons>
                  {socialMediaIcons
                    .filter(item => !!item)
                    .map(item => (
                      <Icon
                        key={item.icon}
                        testId={item.icon}
                        name={item.icon}
                        color={theme.color.black}
                        $hoverColor={theme.color.primary}
                        $clickable
                        onClick={item.onClick}
                        indentLess
                        size={20}
                        $hoverEffect
                      />
                    ))}
                </SocialIcons>
              </Row>

              <ColumnTitle>{t('get-in-touch')}</ColumnTitle>
              <ContactInfo>
                <Icon
                  name={'mail'}
                  color={theme.color.black}
                  $hoverColor={theme.color.primary}
                  $clickable
                  indentLess
                  size={20}
                  $hoverEffect
                />
                {contact?.email}
              </ContactInfo>
              <ContactInfo>
                <Icon
                  name={'phone'}
                  color={theme.color.black}
                  $hoverColor={theme.color.primary}
                  $clickable
                  indentLess
                  size={20}
                  $hoverEffect
                />
                {contact?.phone}
              </ContactInfo>
            </Column>
          </FooterLinksContainer>
        </FooterContentContainer>

        <TermsAndCopyrightsContainer>
          <OptionsContainer>
            <FootNoteOption
              onClick={() => {
                window.location.href = t(ROUTES.IMPRINT_URL);
              }}
            >
              {t('imprint')}
            </FootNoteOption>
            <FootNoteOption
              onClick={() => {
                window.location.href = t(ROUTES.TERMS_AND_CONDITIONS_URL);
              }}
            >
              {t('terms-and-conditions')}
            </FootNoteOption>
            {/*
            <FootNoteOption
              onClick={() => {
                window.location.href = t(ROUTES.NEWSLETTER_UNSUBSCRIBE_URL);
              }}
            >
              {t('unsubscribe')}
            </FootNoteOption>
            */}

            <FootNoteOption
              onClick={() => {
                window.location.href = t(ROUTES.PRIVACY_POLICY_URL);
              }}
            >
              {t('privacy')}
            </FootNoteOption>

            <FootNoteOption
              onClick={() => {
                dispatch(setIsGdprOpen(true));
              }}
            >
              {t('cookies')}
            </FootNoteOption>

            <FootNoteOption
              onClick={() => {
                window.location.href =
                  'https://media.we-conect.com/documents/Code_of_conduct.pdf';
              }}
            >
              {t('code-of-conduct')}
            </FootNoteOption>

            {/*
            <FootNoteOption
              onClick={() => {
                window.location.href = t(ROUTES.PRIVACY_POLICY_URL);
              }}
            >
              {t('people-equity-inclusion')}
            </FootNoteOption>
            */}

            <FootNoteOption
              onClick={() => {
                window.location.href = `${APP_HOST}${t('mappable-company-routes.contact-url')}`;
              }}
            >
              {t('contact-us')}
            </FootNoteOption>
          </OptionsContainer>

          {!isMobile && (
            <CopyrightText>
              <p>
                © {new Date().getFullYear()} we.CONECT Global Leaders Group.
              </p>{' '}
              <p>{t('all-rights-reserved')}</p>
            </CopyrightText>
          )}
        </TermsAndCopyrightsContainer>
      </FooterContainer>
    </FooterRootContainer>
  );
};

export default BlogsFooter;
